import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import RegisterModal from '../components/RegisterModal';
import { api } from '../lib/api';
import { trackEvent } from '../lib/analytics';

// Shared registration state for the whole page:
//   • open/close for the email-registration modal (any CTA on the
//     page can flip it via `openModal()` / `closeModal()`)
//   • the live participant count that drives the milestone bar +
//     counter in `RegisterSection`. Hydrated from `GET /api/count`
//     on mount, then updated synchronously when a registration
//     succeeds — the 201 response from `POST /api/register` carries
//     the new total (see apps/api/src/routes/register.ts) so the
//     section snaps to the new value the moment the modal flips to
//     its success step. No follow-up fetch needed (and the
//     edge-cached `/api/count` would still be stale for up to 60s
//     anyway).
interface RegisterModalContextValue {
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
  participantCount: number;
  setParticipantCount: (count: number) => void;
  // Whether the pre-registration window has closed. Hydrated from the
  // `closed` flag on `GET /api/count` (derived from the configured
  // `closesAt`). Drives the grayed-out/disabled state on every register
  // CTA. Defaults to `false` so CTAs stay active until proven closed —
  // if the count fetch fails or the API hasn't been redeployed with the
  // new field, registration stays open.
  registrationClosed: boolean;
}

// Floor for the displayed counter when `/api/count` is unreachable
// (cold/offline visitor, transient CloudFront/Lambda blip). Keeps the
// progress bar + counter looking populated instead of snapping back to
// zero. Matches the "sensible default" called out in Phase 2 of
// CLAUDE.md.
const FALLBACK_PARTICIPANT_COUNT = 0;

interface CountResponse {
  count: number;
  closesAt?: string | null;
  closed?: boolean;
}

const RegisterModalContext = createContext<RegisterModalContextValue | null>(
  null,
);

interface RegisterModalProviderProps {
  children: ReactNode;
}

// Mount once near the root of the tree (App.tsx). Renders the modal
// itself so descendants only need to call `openModal()` — they don't
// need to render their own <RegisterModal/>.
export function RegisterModalProvider({ children }: RegisterModalProviderProps) {
  const [open, setOpen] = useState(false);
  const [participantCount, setParticipantCount] = useState<number>(
    FALLBACK_PARTICIPANT_COUNT,
  );
  const [registrationClosed, setRegistrationClosed] = useState(false);

  // Single chokepoint for every register CTA that opens the modal
  // (RegisterSection's wooden-plank CTAs, FloatingCTARail mobile
  // bottom-panel register button, etc) — so the analytics event is
  // wired here once instead of being sprinkled across every call
  // site. Scroll-to-anchor CTAs (Hero desktop register link,
  // FloatingCTARail desktop rail `key === 'register'` item) don't
  // call this, and by design they don't fire the open-modal event.
  const openModal = useCallback(() => {
    // Defensive no-op once registration has closed — every CTA is
    // disabled in that state, so this only guards against a stray
    // programmatic call.
    if (registrationClosed) return;
    trackEvent(
      { name: 'OpenRegisterModal' },
      { name: 'open_register_modal' },
    );
    setOpen(true);
  }, [registrationClosed]);
  const closeModal = useCallback(() => setOpen(false), []);

  useEffect(() => {
    let cancelled = false;
    api<CountResponse>('/count')
      .then((data) => {
        if (cancelled) return;
        if (typeof data?.count === 'number' && Number.isFinite(data.count)) {
          setParticipantCount(data.count);
        }
        if (typeof data?.closed === 'boolean') {
          setRegistrationClosed(data.closed);
        }
      })
      .catch(() => {
        // Swallow — the fallback initial state is already in place.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      open,
      openModal,
      closeModal,
      participantCount,
      setParticipantCount,
      registrationClosed,
    }),
    [open, openModal, closeModal, participantCount, registrationClosed],
  );

  return (
    <RegisterModalContext.Provider value={value}>
      {children}
      <RegisterModal open={open} onClose={closeModal} />
    </RegisterModalContext.Provider>
  );
}

// Throws when used outside the provider — that's intentional so a
// stray CTA can't silently no-op if the provider gets removed.
export function useRegisterModal(): RegisterModalContextValue {
  const ctx = useContext(RegisterModalContext);
  if (!ctx) {
    throw new Error(
      'useRegisterModal must be used inside <RegisterModalProvider>',
    );
  }
  return ctx;
}
